import axios from 'axios';
import { authService } from './authService';
import { ScenarioAnswers, Recommendation } from '../types/camera';

const API_BASE_URL = process.env.REACT_APP_API_URL;

if (!API_BASE_URL) {
  console.error('REACT_APP_API_URL environment variable is not set');
}

interface JobResponse {
  jobId: string;
  status: 'processing' | 'completed' | 'failed';
  message?: string;
}

interface JobStatusResponse {
  jobId: string;
  status: 'processing' | 'completed' | 'failed';
  recommendation?: Recommendation;
  error?: string;
  message?: string;
}

class ApiService {
  private getAuthHeaders() {
    const idToken = authService.getIdToken();
    if (!idToken) {
      throw new Error('No authentication token available');
    }

    return {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    };
  }

  async createRecommendationJob(answers: ScenarioAnswers): Promise<string> {
    if (!API_BASE_URL) {
      throw new Error('API URL is not configured');
    }

    if (!authService.isAuthenticated()) {
      throw new Error('User must be authenticated to get recommendations');
    }

    try {
      const response = await axios.post<JobResponse>(
        `${API_BASE_URL}/recommendations`,
        answers,
        {
          headers: this.getAuthHeaders(),
          timeout: 10000, // 10 second timeout for job creation
        }
      );

      if (response.data.jobId) {
        return response.data.jobId;
      } else {
        throw new Error('Invalid response: missing jobId');
      }
    } catch (error) {
      console.error('Job creation failed:', error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          authService.logout();
          throw new Error('Session expired. Please log in again.');
        } else if (error.response && error.response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else if (error.code === 'ECONNABORTED') {
          throw new Error('Request timed out. Please try again.');
        } else if (error.response) {
          throw new Error(`Request failed: ${error.response.status} ${error.response.statusText}`);
        }
      }

      throw new Error('Failed to create recommendation job. Please try again.');
    }
  }

  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    if (!API_BASE_URL) {
      throw new Error('API URL is not configured');
    }

    if (!authService.isAuthenticated()) {
      throw new Error('User must be authenticated to get recommendations');
    }

    try {
      const response = await axios.get<JobStatusResponse>(
        `${API_BASE_URL}/recommendations/${jobId}`,
        {
          headers: this.getAuthHeaders(),
          timeout: 10000, // 10 second timeout for status check
        }
      );

      return response.data;
    } catch (error) {
      console.error('Status check failed:', error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          authService.logout();
          throw new Error('Session expired. Please log in again.');
        } else if (error.response?.status === 404) {
          throw new Error('Job not found or has expired.');
        } else if (error.response && error.response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else if (error.code === 'ECONNABORTED') {
          throw new Error('Request timed out. Please try again.');
        } else if (error.response) {
          throw new Error(`Request failed: ${error.response.status} ${error.response.statusText}`);
        }
      }

      throw new Error('Failed to check job status. Please try again.');
    }
  }

  async getCameraRecommendation(answers: ScenarioAnswers): Promise<Recommendation> {
    // Create job and poll for results
    const jobId = await this.createRecommendationJob(answers);
    console.log('Job created:', jobId);

    // Poll for results
    const maxAttempts = 60; // Poll for up to 2 minutes (60 * 2 seconds)
    const pollInterval = 2000; // 2 seconds

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));

      const status = await this.getJobStatus(jobId);
      console.log(`Poll attempt ${attempt}/${maxAttempts}, status: ${status.status}`);

      if (status.status === 'completed' && status.recommendation) {
        return status.recommendation;
      } else if (status.status === 'failed') {
        throw new Error(status.error || 'Job failed to complete');
      }
      // If status is 'processing', continue polling
    }

    throw new Error('Job timed out after 2 minutes. Please try again.');
  }
}

export const apiService = new ApiService();